import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import { Button, CardBody, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { queryKeyes } from 'shared/queryKeys';
import { RootState } from 'index';
import { useQuery } from 'react-query';
import { apiMedia } from 'global/api.global';
import { queryClient } from 'react-query/queryClient';
import { errorToast, successToast } from 'Components/Toasts';
import ReportingFileUpload from 'Components/ReportingFileUpload';
import { checkInvalidPrimaryKey, dateTimeFormat } from 'GenericForms/Helper';
import { GetPageWithID } from '../pendingpage.hook';
import PageNavButton from '../PageNavButton';
import { EDNFileLimit, FileStatus } from 'shared/constants';
import { FileItem } from 'shared/CommonInterface';
import TableLoading from 'Components/TableLoading';
import Message from 'Components/Message';

export const PendingEDNFiles = () => {
    /** State variables */
    const { VesselID } = useSelector((state: RootState) => state.Reporting);
    const [modalState, setModalState] = useState(false);
    const [fileObject, setFileObject] = useState<any>({});
    const [EDNFiles, setEDNFiles] = useState<Array<any>>([]);
    const [pageUrl, setPageUrl] = useState("");
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [emptyFileError, setEmptyFileError] = useState(false);
    const [cachedReports, setCachedReports] = useState<any>(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [uploading, setUploading] = useState(false);
    /** State variables end */

    const handleSetFile = (files: any[]) => {
        const adaptedFiles: FileItem[] = files.map(file => ({
            name: file.name || '',
            preview: file.preview || '',
            file: file.file || null,
        }));
        setEDNFiles(adaptedFiles); // Update the state with the transformed data
    }

    useEffect(() => {
        if (!checkInvalidPrimaryKey(VesselID)) {
            return;
        }
        setPageUrl(queryKeyes.pagination.EDNPendingFilesPage.url(VesselID, FileStatus.PENDING));
    }, [VesselID]);

    /** 
    * Fetches the list of pending EDN files for a specific vessel. 
    * Uses the `useQuery` hook to load EDN files associated with the selected vessel.
    */
    const { data: fileUploadingObj, isLoading: fileUploadingLoading } = useQuery(
        [queryKeyes.pagination.EDNPendingFilesPage.key, VesselID, pageUrl],
        async () => await GetPageWithID(VesselID, pageUrl), {
        enabled: true,
        onSuccess: (data) => {
            setInitialLoadComplete(true);
            // Update cached reports when new data arrives
            if (data?.results?.length > 0) {
                setCachedReports(data);
            }
        }
    }
    );
    const displayData = fileUploadingObj || cachedReports;
    const page = displayData?.results;

    /**
    * Adds a CSS class to the document's body to handle modal styling.
    */
    function removeBodyCss() {
        document.body.classList.add("no_padding");
    }

    /**
     * Toggles the state of the modal and applies the necessary body styling.
     */
    function tog_backdrop() {
        setModalState(!modalState);
        removeBodyCss();
    }

    /**
    * Sets the modal state to open and stores the file name and object for uploading.
    * 
    * @param file - The file object associated with the selected EDN entry.
    */
    const handleUploadButtonClick = (file: any) => {
        setModalState(!modalState);
        setFileObject(file);
    }

    const handleUpload = async () => {
        setUploading(true);
        const validFiles = EDNFiles.filter(file => file.file !== null && file.file !== undefined);

        if (validFiles.length === 0) {
            setEmptyFileError(true);
            setUploading(false);
            return;
        }

        if (fileObject && EDNFiles && EDNFiles.length > 0) {
            try {
                const finalObj = new FormData();
                finalObj.append("shore_supply", String(fileObject.id));
                EDNFiles.forEach((image: any) => {
                    if (image.file !== null && image.file !== undefined) {
                        finalObj.append("edn_file_uploads", image.file);
                    }
                });
                const res = await apiMedia.post(`shore_supply_edn_file/upload_files/`, finalObj);
                if (res.status === 200 || res.status === 201) {
                    queryClient.invalidateQueries(queryKeyes.pagination.EDNPendingFilesPage.key);
                    queryClient.invalidateQueries(queryKeyes.vessel.ShoreSupplyObject.key);
                    queryClient.invalidateQueries(queryKeyes.vessel.ViewFilesByReport.key);
                    queryClient.invalidateQueries(queryKeyes.vessel.ViewFilesByVessel.key); setRefreshKey(refreshKey + 1);
                    setModalState(false);
                    setEDNFiles([]);
                    successToast("File uploaded successfully!");
                } else {
                    errorToast("Unexpected response, please try again.");
                }
            } catch (err: any) {
                errorToast(err?.response?.data?.detail || "Something went wrong.");
            } finally {
                setUploading(false);
            }
        }
    };


    const showLoadingIndicator = fileUploadingLoading && !initialLoadComplete;
    return (
        <React.Fragment>
            <Row>
                {showLoadingIndicator && <TableLoading />}
            </Row>
            <CardBody className="px-0 py-0 pb-0 mt-2">
                <div className="table-responsive table-height-10">
                    <table className="table mb-0" key={refreshKey}>
                        <thead className="table-light">
                            <tr>
                                <th className="p-2 align-middle sr-no-width">#</th>
                                <th className="p-2 align-middle">Date & time (Local)</th>
                                <th className="p-2 align-middle">Name of report</th>
                                <th className="p-2 align-middle">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {page?.length > 0 ? (
                                page?.map((file: any, index: number) => (
                                    <tr key={index}>
                                        <td className="p-2 align-middle sr-no-width">{index + 1}</td>
                                        <td className="p-2 align-middle" style={{ width: '20%' }}>
                                            {dateTimeFormat(file.reporting_date_time, false, false, true)}
                                        </td>
                                        <td className='p-2 align-middle' style={{ width: '65%' }}>{file.name_of_report}</td>
                                        <td className="p-2 align-middle text-center">
                                            <Button
                                                type="button"
                                                className='btn-sm'
                                                color="primary"
                                                onClick={() => handleUploadButtonClick(file)}>
                                                Upload
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="p-2 align-middle text-center">
                                        <Message message={'No files are pending for upload.'} type="info" width="100%" />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={5} className="p-2 ">
                                    <PageNavButton
                                        setPageUrl={setPageUrl}
                                        pageobj={displayData}
                                        pageUrl={pageUrl}
                                        isLoading={fileUploadingLoading}
                                    />
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </CardBody>
            <Modal
                size='md'
                isOpen={modalState}
                toggle={() => tog_backdrop()}
                backdrop={"static"}
                id="staticBackdrop"
            >
                <ModalHeader className='p-2'>Upload Pending EDN File
                    <button
                        onClick={async () => {
                            setModalState(false);
                            setEDNFiles([]);
                            setEmptyFileError(false);
                        }}
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </ModalHeader>
                <ModalBody className='p-2'>
                    <ReportingFileUpload
                        setFile={handleSetFile}
                        file={Array.isArray(EDNFiles) ? EDNFiles : []}
                        isMultiple={true}
                        maxFiles={EDNFileLimit}
                        invalidateQuery={queryKeyes.vessel.ShoreSupplyObject.key}
                        isFileUploadDisable={EDNFileLimit === EDNFiles?.filter((item: any) => item.file)?.length}
                        CustomAcceptableFileTypes={{
                            'image/jpeg': ['.jpg', '.jpeg'],
                            'image/png': ['.png']
                        }}
                        index={0}
                        sm={5}
                    />
                    {emptyFileError &&
                        <Label className="error margin-ve-0-7">Please select a file to upload.</Label>
                    }
                    <div className="mt-1 mb-5">
                        <Button
                            type="submit"
                            color='primary'
                            className='pos-end'
                            disabled={uploading || !EDNFiles[0]?.file}
                            onClick={() => {
                                handleUpload();
                            }}
                        >  {uploading && <i className="spinner-border spinner-border-sm text-light me-2" />}
                            Save
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
}
